import React from "react";
import { PageHero, CtaBanner, Timeline } from "../pages/PageShell";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import { Cctv, Cpu, Server, Plug, Check } from "../ui/Icons";
import { RetrofitDiagram } from "../ui/Diagrams";
import { useSeo } from "../../lib/seo";

const STAGES = [
  {
    title: "Discovery",
    content:
      "Engage with your team to define strategic objectives and set the vision for enhancing your Vision AI solution.",
    outputs: ["Objectives", "Current-state inventory"],
  },
  {
    title: "Investigation",
    content:
      "Deep dive analysis of current setup to identify limitations and technical challenges.",
    outputs: ["Gap analysis", "Feed quality audit"],
  },
  {
    title: "Planning",
    content:
      "Create detailed roadmap with architecture improvements and project milestones.",
    outputs: ["Roadmap", "Hardware plan"],
  },
  {
    title: "Execution",
    content:
      "Implement enhancements focusing on performance optimization and functionality integration.",
    outputs: ["Upgraded pipeline", "Integrations"],
  },
  {
    title: "Delivery",
    content:
      "Finalize and deploy enhanced solution with comprehensive post-deployment support.",
    outputs: ["Rollout", "Post-deployment support"],
  },
];

const UPGRADES = [
  { icon: Cctv, title: "Existing CCTV & VMS", text: "Add detection, tracking and alerts to Axis, Hikvision, Dahua, Bosch and other camera estates through RTSP and ONVIF." },
  { icon: Cpu, title: "Edge fleets", text: "Extend Jetson Xavier and Orin devices already in the field with new models, quantization and over-the-air updates." },
  { icon: Server, title: "On-premise servers", text: "Put idle GPU and x86 capacity to work with containerized inference and central management." },
  { icon: Plug, title: "Business systems", text: "Push events into SCADA, ERP, ticketing and messaging tools with webhooks and a documented API." },
];

export default function EquipIntelligence() {
  useSeo({
    title: "Equip Intelligence: AI upgrades for existing cameras | Episcope AI",
    description:
      "Add real-time computer vision to the CCTV, video management systems, servers and Jetson fleets you already run. Modular AI upgrades with no forklift replacement.",
    path: "/equip-intelligence",
  });
  return (
    <div className="page">
      <div className="container">
        <PageHero
          eyebrow="Equip Intelligence"
          title="Modular AI upgrades for the infrastructure you already run."
          lead="Smart system integrations that add computer vision to existing cameras, edge devices and servers, so you get intelligence without a forklift replacement."
          chips={["Existing CCTV", "Legacy Jetson fleets", "VMS integrations", "No downtime"]}
        />

        <div className="stack">
          <RetrofitDiagram />
          <section>
            <SectionHead
              eyebrow="What we upgrade"
              title="Intelligence for what is already on the wall."
            />
            <div className="fgrid4">
              {UPGRADES.map((u, i) => (
                <Reveal key={u.title} className="feature card card--hover" delay={i * 0.06}>
                  <span className="icon-box">
                    <u.icon />
                  </span>
                  <h3 className="feature__title">{u.title}</h3>
                  <p className="feature__text">{u.text}</p>
                </Reveal>
              ))}
            </div>
          </section>

          <section className="split">
            <div>
              <SectionHead
                eyebrow="Why upgrade"
                title="Keep the hardware, change what it can do."
              />
              <ul className="check-list">
                {[
                  "Lower cost than replacing cameras and recorders",
                  "Faster rollout: weeks, not quarters",
                  "Models optimized for the compute you have",
                  "Incremental: start with one site or one use case",
                ].map((t) => (
                  <li key={t}>
                    <Check />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <Reveal className="card feature">
              <span className="eyebrow eyebrow--plain">Typical starting points</span>
              <div className="chips">
                {["PPE compliance on existing plant cameras", "Vehicle counting on gate cameras", "Loitering alerts on perimeter CCTV", "Queue analytics from ceiling domes", "Re-deploying older Jetson boards"].map(
                  (c) => (
                    <span key={c} className="chip">
                      {c}
                    </span>
                  ),
                )}
              </div>
            </Reveal>
          </section>

          <section>
            <SectionHead eyebrow="AI integration process" title="Five stages from audit to support." />
            <Timeline items={STAGES} />
          </section>
        </div>

        <CtaBanner
          title="Want to know what your current cameras could do?"
          text="Send us a few sample clips and a camera list. We will come back with a candid assessment and an upgrade plan."
        />
      </div>
    </div>
  );
}
